import { Update } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import * as CategoryActions from "../../../../redux/categories/categories.actions";
import * as CategoryReducers from "../../../../redux/categories/categories.reducers";
import * as ProductActions from "../../../../redux/products/products.actions";
import {
  AppDispatch,
  RootState,
  useAppDispatch,
} from "../../../../redux/store";
import Button3D from "../../../../utils/common/3d-button/Button3D";
import { UploadImageWidget } from "../../../../utils/UploadImageWidget";
import {
  ICategoryResponseView,
  ISubCategoryView,
} from "../../../categories/models/ICategoryResponseView";
import LayoutHeading from "../../../layout/components/layout-heading/LayoutHeading";
import MainNavBar from "../../../layout/pages/navbar/MainNavBar";
import SpinnerUI from "../../../ui/components/spinner/SpinnerUI";
import { ProductPreviewText } from "../edit-product/EditProduct";
import { primary } from "../../../../themes/theme";

const validationSchema = Yup.object().shape({
  categoryId: Yup.string().required("Category is required"),
  subCategoryId: Yup.string().required("Sub Category is required"),
  title: Yup.string().trim().required("Product Name is required"),
  description: Yup.string().trim().required("Description is required"),
  imageUrl: Yup.string().trim().required("Image URL is required"),
  brand: Yup.string().trim().required("Brand is required"),
  price: Yup.number().required("Price is required"),
  quantity: Yup.number().required("Quantity is required"),
});

const UploadProduct: React.FC = () => {
  const dispatch: AppDispatch = useAppDispatch();
  const navigate = useNavigate();
  const [categoryId, setCategoryId] = useState<string>("");
  const [categoryName, setCategoryName] = useState<string>("fashion");
  const [subCategoryId, setSubCategoryId] = useState<string>("");
  const [imageUrl, setImageUrl] = useState<string>("");
  const [subCategories, setSubCategories] = useState<ISubCategoryView[]>([]);
  const cloudinaryRef = useRef<any>();
  const widgetRef = useRef<any>();

  const handleUploadImage = () => {
    widgetRef.current.open();
  };

  // Get all Categories from redux
  const categoryReduxState: CategoryReducers.InitialState = useSelector(
    (state: RootState) => state[CategoryReducers.categoryFeatureKey]
  );

  const { loading, categories } = categoryReduxState;

  useEffect(() => {
    dispatch(CategoryActions.getAllCategoriesAction());
    UploadImageWidget.upload(cloudinaryRef, widgetRef)
      .then((imageUrl) => {
        if (imageUrl) {
          setImageUrl(imageUrl.toString());
          formik.setFieldValue("imageUrl", imageUrl.toString());
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, [dispatch]);

  const handleCategoryChange = (e: any) => {
    const selectedCategoryId = e.target.value;
    setCategoryId(selectedCategoryId);

    const selectedCategory = categories.find(
      (category) => category._id === selectedCategoryId
    );
    setCategoryName(selectedCategory?.name || "");

    formik.setFieldValue("categoryId", selectedCategoryId);

    if (categories.length > 0) {
      let category: ICategoryResponseView | undefined = categories.find(
        (category) => category?._id?.toString() === selectedCategoryId
      );
      if (category && category.subCategories) {
        setSubCategories(category.subCategories);
      } else {
        setSubCategories([] as ISubCategoryView[]);
      }
    }
  };

  const handleSubCategoryChange = (e: any) => {
    const selectedSubCategoryId = e.target.value;
    setSubCategoryId(selectedSubCategoryId);
    formik.setFieldValue("subCategoryId", selectedSubCategoryId);
  };

  const handleAddProduct = async (values: any) => {
    const response = await dispatch(
      ProductActions.createProductAction({
        product: values,
      })
    );

    if (response && !response.error) {
      // formik.resetForm();
      // setCategoryId("");
      // setSubCategoryId("");
      // setImageUrl("");
      navigate(`/products/${categoryName.toLowerCase()}`);
    }
  };

  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      imageUrl: "",
      brand: "",
      price: 0,
      quantity: 0,
      categoryId: "",
      subCategoryId: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      await handleAddProduct(values);
    },
  });

  return (
    <>
      <Box>
        {loading && <SpinnerUI />}
        <MainNavBar />
        <LayoutHeading
          heading="Upload Product"
          description="Seamlessly add new products with images, descriptions, and pricing for quick listings."
        />
        <Grid
          container
          sx={{ gap: "10px", pl: { xs: 1, md: 9, lg: 10 }, pr: 1 }}
        >
          <Grid
            item
            xs={12}
            md={5}
            component={Paper}
            elevation={0}
            sx={{
              p: 2,
              borderRadius: "10px",
              height: "100%",
              position: "sticky",
              top: 90,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: "Ubuntu",
                  fontSize: "17px",
                  letterSpacing: "1px",
                  fontWeight: "550",
                }}
              >
                | Add Product Details
              </Typography>
            </Box>
            <Divider
              sx={{
                mt: 1,
                mb: 2,
                color: primary[900],
                border: "1px dashed #f1f2f3",
              }}
            />
            <form onSubmit={formik.handleSubmit}>
              <FormControl fullWidth>
                <Button
                  fullWidth
                  variant="outlined"
                  color="inherit"
                  sx={{
                    mb: 2,
                    textTransform: "capitalize",
                    border: "1px dashed grey",
                  }}
                  onClick={() => handleUploadImage()}
                >
                  Upload Image
                </Button>
                {formik.touched.imageUrl && formik.errors.imageUrl && (
                  <FormHelperText error>
                    {formik.errors.imageUrl}
                  </FormHelperText>
                )}
              </FormControl>
              <FormControl
                variant="outlined"
                fullWidth
                sx={{ minWidth: 120, mb: 2 }}
                size="small"
                error={
                  formik.touched.categoryId && Boolean(formik.errors.categoryId)
                }
              >
                <InputLabel id="demo-select-small-label">Category</InputLabel>
                <Select
                  labelId="demo-select-small-label"
                  id="demo-select-small"
                  value={categoryId}
                  label="Category"
                  onChange={handleCategoryChange}
                >
                  <MenuItem value="">
                    <em>Select Category</em>
                  </MenuItem>
                  {categories.map((category, index) => (
                    <MenuItem key={index} value={category._id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.categoryId && formik.errors.categoryId && (
                  <FormHelperText>{formik.errors.categoryId}</FormHelperText>
                )}
              </FormControl>
              <FormControl
                variant="outlined"
                fullWidth
                sx={{ minWidth: 120, mb: 2 }}
                size="small"
                error={
                  formik.touched.subCategoryId &&
                  Boolean(formik.errors.subCategoryId)
                }
              >
                <InputLabel id="demo-select-small-label">
                  Sub Category
                </InputLabel>
                <Select
                  labelId="demo-select-small-label"
                  id="demo-select-small"
                  value={subCategoryId}
                  label="Sub Category"
                  onChange={handleSubCategoryChange}
                >
                  <MenuItem value="">
                    <em>Select Sub Category</em>
                  </MenuItem>
                  {subCategories.map((subCategory, index) => (
                    <MenuItem key={index} value={subCategory._id}>
                      {subCategory.name}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.subCategoryId &&
                  formik.errors.subCategoryId && (
                    <FormHelperText>
                      {formik.errors.subCategoryId}
                    </FormHelperText>
                  )}
              </FormControl>
              <TextField
                sx={{ mb: 2 }}
                type="text"
                placeholder="Product Title"
                size="small"
                label="Product Title"
                variant="outlined"
                {...formik.getFieldProps("title")}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={formik.touched.title && formik.errors.title}
                fullWidth
              />
              <TextField
                sx={{ mb: 2 }}
                type="text"
                placeholder="Brand"
                size="small"
                label="Brand"
                variant="outlined"
                {...formik.getFieldProps("brand")}
                error={formik.touched.brand && Boolean(formik.errors.brand)}
                helperText={formik.touched.brand && formik.errors.brand}
                fullWidth
              />

              <TextField
                label="Description"
                variant="outlined"
                fullWidth
                multiline
                rows={2}
                sx={{ mb: 2 }}
                {...formik.getFieldProps("description")}
                error={
                  formik.touched.description &&
                  Boolean(formik.errors.description)
                }
                helperText={
                  formik.touched.description && formik.errors.description
                }
              />

              <TextField
                sx={{ mb: 2 }}
                type="number"
                placeholder="Price"
                size="small"
                label="Price"
                variant="outlined"
                {...formik.getFieldProps("price")}
                error={formik.touched.price && Boolean(formik.errors.price)}
                helperText={formik.touched.price && formik.errors.price}
                fullWidth
              />
              <TextField
                sx={{ mb: 2 }}
                type="number"
                placeholder="Quantity"
                size="small"
                label="Quantity"
                variant="outlined"
                {...formik.getFieldProps("quantity")}
                error={
                  formik.touched.quantity && Boolean(formik.errors.quantity)
                }
                helperText={formik.touched.quantity && formik.errors.quantity}
                fullWidth
              />
              <Box sx={{ py: 1 }}>
                <Button3D
                  type="submit"
                  buttonText="Upload Product"
                  buttonIcon={
                    <Update
                      fontSize="small"
                      sx={{ pr: "5px", pt: "2px", mt: "2px" }}
                    />
                  }
                />
              </Box>
            </form>
          </Grid>

          <Grid
            item
            xs={12}
            md={5}
            component={Paper}
            elevation={0}
            sx={{ p: 2, borderRadius: "10px", height: "100%" }}
          >
            <Card elevation={0} sx={{ background: "transparent" }}>
              <Box>
                <Typography
                  sx={{
                    fontFamily: "Ubuntu",
                    fontSize: "17px",
                    letterSpacing: "1px",
                    fontWeight: "550",
                  }}
                >
                  | Product Preview
                </Typography>
              </Box>
              <Divider
                sx={{
                  mt: 1,
                  mb: 2,
                  color: primary[900],
                  border: "1px dashed #f1f2f3",
                }}
              />
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    mb: 2,
                  }}
                >
                  {imageUrl && imageUrl ? (
                    <img
                      src={imageUrl}
                      alt="product"
                      style={{
                        width: "225px",
                        height: "250px",
                        objectFit: "cover",
                        borderRadius: "10px",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        m: "auto",
                        p: "auto",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        sx={{ fontFamily: "Ubuntu", fontWeight: "400" }}
                      >
                        No Image Selected
                      </Typography>
                    </Box>
                  )}
                </Box>
                <ProductPreviewText
                  primary={"Name"}
                  secondary={formik.values.title}
                />
                <ProductPreviewText
                  primary={"Brand"}
                  secondary={formik.values.brand}
                />

                <ProductPreviewText
                  primary={"Category"}
                  secondary={
                    categories.find(
                      (cat) => cat._id === formik.values.categoryId
                    )?.name
                  }
                />
                <ProductPreviewText
                  primary={"Sub Category"}
                  secondary={
                    subCategories.find(
                      (sub) => sub._id === formik.values.subCategoryId
                    )?.name
                  }
                />
                <ProductPreviewText
                  primary={"Price"}
                  secondary={`₹${formik.values.price}`}
                />
                <ProductPreviewText
                  primary={"Quantity"}
                  secondary={formik.values.quantity}
                />
                <ProductPreviewText
                  primary={"Description"}
                  secondary={formik.values.description}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default UploadProduct;
